import React, { useState, useRef } from "react";
import {
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  makeStyles,
  Snackbar,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import { OpenInNew } from "@material-ui/icons";

import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { SortIcon, EditIcon, Delete } from "../../../../LeftNav/SvgIcons";

import { contactsTypesActions } from "../../../../../../store/contactTypes/contactTypesActions";

const useStyles = makeStyles(theme => ({
  selectedItems: {
    textAlign: "right",
    color: "#8D99A5",
    fontSize: "14px",
    fontWeight: "400",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  gridTable: {
    margin: "20px 0px",
  },
  gridHeaderInner: {
    color: "#A9B2BC",
    fontSize: "12px",
    fontWeight: "700",
    paddingTop: "16px",
    paddingBottom: "16px",
  },
  gridHeaderInnerCenter: {
    color: "#A9B2BC",
    fontSize: "12px",
    fontWeight: "700",
    paddingTop: "4px",
    paddingBottom: "4px",
    textAlign: "center",
  },
  gridBodyInner: {
    color: "#464E5F",
    fontSize: "14px",
    fontWeight: "400",
    paddingTop: "16px",
    paddingBottom: "16px",
  },
  gridBodyInnerCenter: {
    color: "#464E5F",
    fontSize: "14px",
    fontWeight: "400",
    paddingTop: "16px",
    paddingBottom: "16px",
    textAlign: "center",
  },
  gridHeader: {
    background: "#E5F2FF",
    borderRadius: "6px",
  },
  gridHeaderRow: {
    borderRadius: "6px",
  },
  gridIconCells: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "15px",
    "& span": {
      cursor: "pointer",
    },
  },
  csvFile: {
    marginTop: "10px",
    display: "flex",
    justifyContent: "flex-end",
  },
  actionButtons: {
    padding: "unset",
    "&:hover": {
      backgroundColor: "transparent",
      boxShadow: "none",
    },
  },
  prospectDetailsButton: {
    color: "#3699FF",
    fontSize: "12px",
    fontWeight: "bold",
    padding: "4px 8px",
    border: "1px solid #3699FF",
    borderRadius: "4px",
    textTransform: "none",
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "rgba(54, 153, 255, 0.04)",
    },
  },
  clickableName: {
    cursor: "pointer",
    color: "#3699FF",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  fileInput: {
    display: "none",
  },
  uploadButton: {
    backgroundColor: "#3699FF",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    "&:hover": {
      backgroundColor: "#2d80d9",
    },
    "&:disabled": {
      backgroundColor: "#cccccc",
      cursor: "not-allowed",
    },
  },
  fileName: {
    marginLeft: "10px",
    color: "#464E5F",
    fontSize: "14px",
  },
  uploadSection: {
    display: "flex",
    alignItems: "center",
    padding: "10px 0px",
  },
  fileInfo: {
    marginLeft: "10px",
    color: "#464E5F",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  fileTypeBadge: {
    backgroundColor: "#E5F2FF",
    color: "#3699FF",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "500",
  },
  callButton: {
    padding: "6px",
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "transparent",
      "& svg": {
        transform: "scale(1.1)",
      },
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    transition: "all 0.2s ease",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
  },
}));

const EmailTemplateGrid = ({
  onOrderBy,
  order,
  templateData,
  loadEmailTemplates,
  isLoading,
  setLoading,
  onEditTemplateClick,
  onDeleteTemplateClick,
  deleteLoading,
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Handle edit button click
  const handleEditClick = template => {
    if (onEditTemplateClick) {
      onEditTemplateClick(template);
    }
  };

  // Handle delete button click
  const handleDeleteClick = template => {
    if (onDeleteTemplateClick) {
      onDeleteTemplateClick(template);
    }
  };

  // Close snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div style={{ position: "relative" }}>
      {isLoading && (
        <div className={classes.loadingOverlay}>
          <CircularProgress />
        </div>
      )}

      <Paper>
        <Table className={classes.gridTable}>
          <TableHead className={classes.gridHeader}>
            <TableRow className={classes.gridHeaderRow}>
              <TableCell className={classes.gridHeaderInner}>
                Title
                <TableSortLabel
                  active={order?.orderByKey === "TemplateName"}
                  direction={order?.orderVal === 1 ? "asc" : "desc"}
                  onClick={() =>
                    onOrderBy({
                      orderByKey: "TemplateName",
                      orderVal: order?.orderVal === 0 ? 1 : 0,
                    })
                  }
                  IconComponent={SortIcon}
                  className='sortIcon'
                />
              </TableCell>
              <TableCell className={classes.gridHeaderInner}>
                Subject
                <TableSortLabel
                  active={order?.orderByKey === "Subject"}
                  direction={order?.orderVal === 1 ? "asc" : "desc"}
                  onClick={() =>
                    onOrderBy({
                      orderByKey: "Subject",
                      orderVal: order?.orderVal === 0 ? 1 : 0,
                    })
                  }
                  IconComponent={SortIcon}
                  className='sortIcon'
                />
              </TableCell>

              <TableCell className={classes.gridHeaderInnerCenter}>
                Created On
                <TableSortLabel
                  active={order?.orderByKey === "CreatedOn"}
                  direction={order?.orderVal === 1 ? "asc" : "desc"}
                  onClick={() =>
                    onOrderBy({
                      orderByKey: "CreatedOn",
                      orderVal: order.orderVal === 0 ? 1 : 0,
                    })
                  }
                  IconComponent={SortIcon}
                  className='sortIcon'
                />
              </TableCell>

              <TableCell className={classes.gridHeaderInnerCenter}>
                Quick Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templateData?.length > 0 ? (
              templateData.map((item, index) => (
                <TableRow key={index} className='gridBodyRow'>
                  <TableCell className={classes.gridBodyInner}>
                    {item.templateName || "--"}
                  </TableCell>
                  <TableCell className={classes.gridBodyInner}>
                    {item.subject || "--"}
                  </TableCell>

                  <TableCell className={classes.gridBodyInnerCenter}>
                    {item.createdOn
                      ? moment(item.createdOn).format("DD-MM-YYYY")
                      : "--"}
                  </TableCell>

                  <TableCell className={classes.gridIconCells}>
                    <Tooltip title='Edit Template' placement='top'>
                      <IconButton
                        className={classes.actionButtons}
                        onClick={() => handleEditClick(item)}
                        disabled={deleteLoading}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Delete Template' placement='top'>
                      <IconButton
                        className={classes.actionButtons}
                        onClick={() => handleDeleteClick(item)}
                        disabled={deleteLoading}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow key='0' className='gridBodyRow'>
                <TableCell
                  colSpan='4'
                  className={classes.gridBodyInner}
                  style={{ textAlign: "center" }}>
                  <span className='emptyGridText'>
                    {isLoading ? "Loading..." : "No Template Available."}
                  </span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </div>
  );
};

export default EmailTemplateGrid;
